import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, LogIn, LogOut, User } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from './AuthModal';

const navHrefs = ['#top', '#catalog', '#production', '/dealer', '#cases', '#news', '#certificates', '#faq', '#contacts', '/brand'];

const HEADER_AUTH = {
  en: { signIn: 'SIGN IN', signOut: 'SIGN OUT' },
  es: { signIn: 'ENTRAR',  signOut: 'SALIR' },
  he: { signIn: 'כניסה',   signOut: 'יציאה' },
};

const Header = () => {
  const { lang, setLang, t } = useLanguage();
  const { user, isAuthenticated, logout } = useAuth();
  const authLabels = HEADER_AUTH[lang] || HEADER_AUTH.en;
  const [isScrolled,      setIsScrolled]      = useState(false);
  const [mobileMenuOpen,  setMobileMenuOpen]  = useState(false);
  const [langMenuOpen,    setLangMenuOpen]     = useState(false);
  const [authModalOpen,   setAuthModalOpen]    = useState(false);
  const [userMenuOpen,    setUserMenuOpen]     = useState(false);
  const langRef    = useRef(null);
  const userRef    = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangMenuOpen(false);
      }
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const scrollToSection = (href) => {
    setMobileMenuOpen(false);

    // Standalone pages (/dealer, /brand, …) → new tab like a separate site area
    if (href.startsWith('/')) {
      const url = href.startsWith('http') ? href : `${window.location.origin}${href}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    // If we're on a sub-page (/dealer, /dealer/apply), navigate back to homepage + anchor
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }

    // Normal scroll on homepage
    const id = href.replace('#', '');
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const switchLang = (code) => {
    setLang(code);
    setLangMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#0a0a0f]/95 backdrop-blur-md border-b border-[#39ff14]/20' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <button
            type="button"
            className="flex items-center cursor-pointer group shrink-0 mr-3 py-1 pr-2 text-left bg-transparent border-0"
            onClick={() => scrollToSection('#top')}
            aria-label="Home"
          >
            <img
              src={`${process.env.PUBLIC_URL || ''}/logo-site.png`}
              alt="H2 Element"
              className="h-8 w-auto sm:h-9 lg:h-10 max-h-12 object-contain object-left transition-opacity duration-300 group-hover:opacity-90"
              width={180}
              height={48}
              decoding="async"
            />
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {t.nav.map((label, i) => (
              <button
                key={label}
                onClick={() => scrollToSection(navHrefs[i])}
                className="relative px-3 py-2 text-[11px] font-semibold tracking-[0.2em] text-white/85 hover:text-[#39ff14] transition-all duration-300 group"
                style={{ fontFamily: 'Rajdhani' }}
              >
                {label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[1px] bg-[#39ff14] group-hover:w-3/4 transition-all duration-300" />
              </button>
            ))}
          </nav>

          {/* Language + Auth + Mobile Toggle */}
          <div className="flex items-center gap-3">

            {/* ── Auth area ── */}
            {isAuthenticated ? (
              /* User menu */
              <div className="relative hidden lg:block" ref={userRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-1 border border-[#39ff14]/30 hover:border-[#39ff14]/70 transition-colors"
                >
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-5 h-5 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-5 h-5 rounded-full bg-[#39ff14]/20 flex items-center justify-center">
                      <span className="text-[9px] font-bold text-[#39ff14]" style={{ fontFamily: 'Share Tech Mono' }}>
                        {(user?.name || user?.email || '?')[0].toUpperCase()}
                      </span>
                    </div>
                  )}
                  <span className="text-[11px] font-semibold text-[#39ff14] tracking-wider max-w-[90px] truncate" style={{ fontFamily: 'Share Tech Mono' }}>
                    {user?.name || user?.email}
                  </span>
                  <ChevronDown size={11} className={`text-[#39ff14]/70 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {userMenuOpen && (
                  <div
                    className="absolute right-0 top-full mt-1 bg-[#0a0a0f] border border-[#39ff14]/20 min-w-[140px] z-50"
                    style={{ boxShadow: '0 0 15px rgba(57,255,20,0.08)' }}
                  >
                    <div className="px-4 py-2 border-b border-[#39ff14]/10">
                      <p className="text-[10px] text-white/40 truncate" style={{ fontFamily: 'Share Tech Mono' }}>
                        {user?.email}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { logout(); setUserMenuOpen(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-xs font-semibold tracking-wider text-red-400/70 hover:text-red-400 hover:bg-red-500/5 transition-colors"
                      style={{ fontFamily: 'Share Tech Mono' }}
                    >
                      <LogOut size={12} />
                      {authLabels.signOut}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* Login button */
              <button
                type="button"
                onClick={() => setAuthModalOpen(true)}
                className="hidden lg:flex items-center gap-2 px-3 py-1 border border-[#39ff14]/30 hover:border-[#39ff14]/70 text-[#39ff14]/70 hover:text-[#39ff14] transition-all duration-200"
                style={{ fontFamily: 'Share Tech Mono' }}
              >
                <LogIn size={12} />
                <span className="text-[11px] font-semibold tracking-wider">{authLabels.signIn}</span>
              </button>
            )}

            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1 cursor-pointer px-3 py-1 border border-[#00f0ff]/30 hover:border-[#00f0ff] transition-colors"
              >
                <span className="text-xs font-semibold text-[#00f0ff] tracking-wider" style={{ fontFamily: 'Share Tech Mono' }}>
                  {lang.toUpperCase()}
                </span>
                <ChevronDown size={12} className={`text-[#00f0ff] transition-transform duration-200 ${langMenuOpen ? 'rotate-180' : ''}`} />
              </button>

              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-1 bg-[#0a0a0f] border border-[#00f0ff]/20 min-w-[80px] z-50"
                     style={{ boxShadow: '0 0 15px rgba(0,240,255,0.1)' }}>
                  {['en', 'es', 'he'].map((code) => (
                    <button
                      key={code}
                      onClick={() => switchLang(code)}
                      className={`w-full px-4 py-2 text-xs font-semibold tracking-wider text-left transition-colors ${
                        lang === code ? 'text-[#39ff14] bg-[#39ff14]/5' : 'text-[#00f0ff]/60 hover:text-[#00f0ff] hover:bg-[#00f0ff]/5'
                      }`}
                      style={{ fontFamily: 'Share Tech Mono' }}
                    >
                      {code.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              className="lg:hidden p-2 text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-[#0a0a0f]/98 backdrop-blur-xl border-t border-[#39ff14]/20">
          <div className="px-4 py-4 space-y-1">
            {t.nav.map((label, i) => (
              <button
                key={label}
                onClick={() => scrollToSection(navHrefs[i])}
                className="block w-full text-left py-2 px-3 text-sm font-semibold text-white/85 hover:text-[#39ff14] hover:bg-[#39ff14]/5 border-l-2 border-transparent hover:border-[#39ff14] transition-all"
              >
                {label}
              </button>
            ))}

            {/* Mobile auth row */}
            <div className="pt-2 border-t border-[#39ff14]/10">
              {isAuthenticated ? (
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2">
                    {user?.avatar ? (
                      <img src={user.avatar} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-[#39ff14]/20 flex items-center justify-center">
                        <span className="text-[9px] font-bold text-[#39ff14]" style={{ fontFamily: 'Share Tech Mono' }}>
                          {(user?.name || user?.email || '?')[0].toUpperCase()}
                        </span>
                      </div>
                    )}
                    <span className="text-xs text-white/60 truncate max-w-[140px]" style={{ fontFamily: 'Share Tech Mono' }}>
                      {user?.name || user?.email}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { logout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-1 text-[11px] text-red-400/70 hover:text-red-400 transition-colors"
                    style={{ fontFamily: 'Share Tech Mono' }}
                  >
                    <LogOut size={12} /> {authLabels.signOut}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => { setAuthModalOpen(true); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full py-2 px-3 text-sm font-semibold text-[#39ff14]/70 hover:text-[#39ff14] hover:bg-[#39ff14]/5 border-l-2 border-transparent hover:border-[#39ff14] transition-all"
                >
                  <LogIn size={14} /> {authLabels.signIn}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auth modal */}
      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </header>
  );
};

export default Header;
